import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2, 
  Key,
  Shield,
  User,
  Loader2,
  ArrowLeft,
  UserCheck,
  UserX,
  Eye,
  Copy,
  Check,
  Mail,
  Monitor,
  Globe,
  Clock,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  History
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Login history (sign-in activity)
  const [lhItems, setLhItems] = useState([]);
  const [lhLoading, setLhLoading] = useState(false);
  const [lhTotal, setLhTotal] = useState(0);
  const [lhPage, setLhPage] = useState(1);
  const [lhSearch, setLhSearch] = useState("");
  const [lhUser, setLhUser] = useState(null); // {id, label} when filtered to one user
  const LH_PAGE_SIZE = 25;
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: "user"
  });
  const [newPassword, setNewPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginHistory = async (page = lhPage, search = lhSearch, userId = lhUser?.id) => {
    try {
      setLhLoading(true);
      const params = { page, page_size: LH_PAGE_SIZE };
      if (search) params.search = search;
      if (userId) params.user_id = userId;
      const response = await axios.get(`${API}/auth/login-history`, { params });
      setLhItems(response.data.items || []);
      setLhTotal(response.data.total || 0);
    } catch (err) {
      console.error("Login history fetch error:", err);
    } finally {
      setLhLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchLoginHistory(1);
  }, []);

  useEffect(() => {
    fetchLoginHistory(lhPage, lhSearch, lhUser?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lhPage, lhUser]);

  const lhTimestamp = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-GB", {
        timeZone: "Europe/London",
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const lhTotalPages = Math.max(1, Math.ceil(lhTotal / LH_PAGE_SIZE));

  const handleCreateUser = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error("Please enter first name, last name and email");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success(`Invite sent to ${formData.email}`, {
        description: "They'll get an email to verify and set their password.",
      });
      setCreateDialogOpen(false);
      setFormData({ first_name: "", last_name: "", email: "", role: "user" });
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to create user";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvite = async (userObj) => {
    try {
      await axios.post(`${API}/auth/users/${userObj.id}/resend-invite`);
      toast.success(`Invite re-sent to ${userObj.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend invite");
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      await axios.put(`${API}/auth/users/${selectedUser.id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        is_active: formData.is_active
      });
      toast.success("User updated successfully");
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to update user";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    // Empty newPassword ⇒ backend auto-generates a strong random one.
    // We forward whatever the admin typed as-is (backend enforces min 8 chars).
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword || null,
      });
      // Show credentials dialog with the new password.
      setCreatedCredentials({
        email: response.data.email || selectedUser.email || "",
        password: response.data.new_password,
        name: selectedUser.name,
        role: selectedUser.role,
        isReset: true,
      });
      setResetPasswordDialogOpen(false);
      setCredentialsDialogOpen(true);
      setSelectedUser(null);
      setNewPassword("");
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/auth/users/${selectedUser.id}`);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to delete user";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      role: user.role,
      is_active: user.is_active
    });
    setEditDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-700';
      case 'viewer':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-5 h-5 text-purple-600" />;
      case 'viewer':
        return <Eye className="w-5 h-5 text-gray-600" />;
      default:
        return <User className="w-5 h-5 text-blue-600" />;
    }
  };

  const getRoleIconBg = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100';
      case 'viewer':
        return 'bg-gray-100';
      default:
        return 'bg-blue-100';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'viewer':
        return 'Viewer (Read-only)';
      default:
        return 'User';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-1 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h1 className="font-heading text-2xl font-bold text-gray-900" data-testid="admin-page-title">
                    User Management
                  </h1>
                  <p className="text-gray-500 mt-1">
                    Create and manage user accounts
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(isAdmin ? "/home" : "/projects")}
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setFormData({ first_name: "", last_name: "", email: "", role: "user" });
                    setCreateDialogOpen(true);
                  }}
                  data-testid="create-user-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </div>
          </div>

          {/* Users List */}
          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-20 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-fade-in delay-100">
              <div className="divide-y divide-gray-100">
                {users.map((user, idx) => (
                  <div 
                    key={user.id}
                    className={`p-4 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors ${
                      !user.is_active ? 'opacity-60' : ''
                    }`}
                    data-testid={`user-row-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getRoleIconBg(user.role)}`}>
                        {getRoleIcon(user.role)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{user.name}</h3>
                          <Badge className={`text-xs ${getRoleBadgeStyle(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </Badge>
                          {!user.is_active && (
                            <Badge variant="outline" className="text-xs text-red-600 border-red-200">
                              Disabled
                            </Badge>
                          )}
                          {!user.is_verified && (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-200" data-testid={`pending-badge-${idx}`}>
                              Pending invite
                            </Badge>
                          )}
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                              You
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            @{user.username}
                          </span>
                          {user.email && (
                            <span className="text-xs text-gray-500 font-mono" title={user.email}>
                              {user.email}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            Created {formatDate(user.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                        onClick={() => {
                          setLhUser({ id: user.id, label: user.name });
                          setLhPage(1);
                          document.querySelector('[data-testid="login-activity-section"]')?.scrollIntoView({ behavior: "smooth" });
                        }}
                        data-testid={`view-signins-${idx}`}
                        title="View this user's sign-ins"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      {!user.is_verified && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          onClick={() => handleResendInvite(user)}
                          data-testid={`resend-invite-${idx}`}
                          title="Resend invite email"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                        onClick={() => openEditDialog(user)}
                        data-testid={`edit-user-${idx}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-500 hover:text-amber-600 hover:bg-amber-50"
                        onClick={() => {
                          setSelectedUser(user);
                          setNewPassword("");
                          setResetPasswordDialogOpen(true);
                        }}
                        data-testid={`reset-password-${idx}`}
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-user-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Login Activity */}
          <div className="mt-10 animate-fade-in delay-200" data-testid="login-activity-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-600" />
                  Login Activity
                </h2>
                <p className="text-sm text-gray-500">Recent sign-ins across all users (last 90 days)</p>
              </div>
              <div className="flex items-center gap-2">
                {lhUser && (
                  <Badge variant="outline" className="text-xs text-blue-700 border-blue-200">
                    {lhUser.label}
                    <button
                      className="ml-1.5 text-blue-500 hover:text-blue-800"
                      onClick={() => { setLhUser(null); setLhPage(1); }}
                      data-testid="lh-clear-user-filter"
                    >
                      ✕
                    </button>
                  </Badge>
                )}
                <Input
                  placeholder="Search name or email"
                  value={lhSearch}
                  onChange={(e) => setLhSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { setLhPage(1); fetchLoginHistory(1, lhSearch, lhUser?.id); } }}
                  className="h-9 w-56"
                  data-testid="lh-search"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setLhPage(1); fetchLoginHistory(1, lhSearch, lhUser?.id); }}
                  disabled={lhLoading}
                  data-testid="lh-refresh"
                >
                  {lhLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {lhItems.length === 0 ? (
                <div className="p-12 text-center text-sm text-gray-400" data-testid="lh-empty">
                  {lhLoading ? "Loading sign-ins…" : "No sign-ins recorded yet."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="lh-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                        <th className="px-4 py-3">User</th>
                        <th className="px-4 py-3">Device</th>
                        <th className="px-4 py-3">IP</th>
                        <th className="px-4 py-3">When</th>
                        <th className="px-4 py-3 text-right">Audit log</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {lhItems.map((it, idx) => (
                        <tr key={idx} className="hover:bg-gray-50" data-testid={`lh-row-${idx}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{it.name || it.username}</div>
                            <div className="text-xs text-gray-500 font-mono">{it.email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 text-gray-700" title={it.user_agent}>
                              <Monitor className="w-3.5 h-3.5 text-gray-400" />
                              {it.device}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 font-mono text-gray-600">
                              <Globe className="w-3.5 h-3.5 text-gray-400" />
                              {it.ip || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 text-gray-700">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              {lhTimestamp(it.at)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                              onClick={() => navigate(`/admin/stats?activityUser=${encodeURIComponent(it.username || it.email)}`)}
                              data-testid={`lh-audit-link-${idx}`}
                              title="View this user's activity in the audit log"
                            >
                              View <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {lhTotal > LH_PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
                  <span>Page {lhPage} of {lhTotalPages} · {lhTotal} sign-ins</span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={lhPage <= 1 || lhLoading} onClick={() => setLhPage((p) => Math.max(1, p - 1))} data-testid="lh-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={lhPage >= lhTotalPages || lhLoading} onClick={() => setLhPage((p) => Math.min(lhTotalPages, p + 1))} data-testid="lh-next">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="create-first">First Name</Label>
                <Input
                  id="create-first"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="create-user-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-last">Last Name</Label>
                <Input
                  id="create-last"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="create-user-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="user@nassauengineering.ie"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="create-user-email"
              />
              <p className="text-xs text-gray-500">
                They'll receive an email to verify their address and set their own password.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Regular User</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-user-submit"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog - shown after create/reset */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {createdCredentials?.isReset ? 'Password Reset Complete' : 'User Created Successfully'}
            </DialogTitle>
            <DialogDescription>
              Save these credentials - the password cannot be retrieved later
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Name</p>
                <p className="font-medium text-gray-900">{createdCredentials?.name}</p>
              </div>
              {createdCredentials?.username && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Username</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded border text-sm flex-1">
                      {createdCredentials?.username}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(createdCredentials?.username, 'username')}
                    >
                      {copiedField === 'username' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
              {createdCredentials?.email && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Email <span className="text-gray-400">(send credentials here)</span></p>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded border text-sm flex-1">
                      {createdCredentials.email}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(createdCredentials.email, 'email')}
                    >
                      {copiedField === 'email' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 mb-1">Password</p>
                <div className="flex items-center gap-2">
                  <code className="bg-white px-2 py-1 rounded border text-sm flex-1 font-mono">
                    {createdCredentials?.password}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(createdCredentials?.password, 'password')}
                  >
                    {copiedField === 'password' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Role</p>
                <Badge className={`text-xs ${getRoleBadgeStyle(createdCredentials?.role)}`}>
                  {getRoleLabel(createdCredentials?.role)}
                </Badge>
              </div>
            </div>
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <Key className="w-3 h-3" />
              Make sure to save these credentials securely
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setCredentialsDialogOpen(false);
                setCreatedCredentials(null);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-first">First Name</Label>
                <Input
                  id="edit-first"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="edit-user-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last">Last Name</Label>
                <Input
                  id="edit-last"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="edit-user-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                disabled={selectedUser?.id === currentUser?.id}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Regular User</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              {selectedUser?.id === currentUser?.id && (
                <p className="text-xs text-amber-600">You cannot change your own role</p>
              )}
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Account Status</Label>
                <p className="text-sm text-gray-500">
                  {formData.is_active ? 'User can log in' : 'User is blocked'}
                </p>
              </div>
              <Button
                variant={formData.is_active ? "outline" : "default"}
                size="sm"
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                disabled={selectedUser?.id === currentUser?.id}
                data-testid="edit-user-status"
              >
                {formData.is_active ? (
                  <>
                    <UserX className="w-4 h-4 mr-1" />
                    Disable
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-1" />
                    Enable
                  </>
                )}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditUser} 
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="edit-user-submit"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <span className="font-semibold">{selectedUser?.name}</span>
              {selectedUser?.email && <> · <span className="font-mono text-xs">{selectedUser.email}</span></>}
              {". "}
              Leave the field blank to generate a strong random password automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password (optional)</Label>
              <Input
                id="new-password"
                type="text"
                placeholder="Leave blank to auto-generate"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-password-input"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500">
                The new password will be shown once so you can email it to the user. It is not stored in plain text on the server.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={isSubmitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reset-password-submit"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {newPassword ? "Set password" : "Generate & reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedUser?.name}&apos;s account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-user-confirm"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
